import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const GIFT_PROPERTY = '_cart_gift_product';

class CartGiftProduct extends HTMLElement {
  #loadingCount = 0;
  #checkoutWasDisabled = null;

  connectedCallback() {
    this.addEventListener('change', this.onChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.syncGiftProductState().catch((error) => console.error(error));
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  onChange = async (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;

    const variantId = Number(this.dataset.giftVariantId);
    if (!variantId) return;

    try {
      await this.withLoading(() =>
        input.checked ? this.addGiftProduct(variantId) : this.removeGiftProduct(variantId)
      );
    } catch (error) {
      input.checked = !input.checked;
      console.error(error);
    }
  };

  getSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    return Array.from(sectionIds);
  }

  async addGiftProduct(variantId) {
    const cart = await this.getCart();
    const existingGiftLine = this.findGiftLine(cart, variantId);

    if (existingGiftLine) {
      this.dispatchCartUpdate(cart, {});
      return;
    }

    const sections = this.getSectionIds();

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: 'true',
              },
            },
          ],
          sections,
          sections_url: window.location.pathname,
        }),
      }),
    });

    const parsedResponse = await response.json();

    if (!response.ok || parsedResponse.status) {
      throw new Error(parsedResponse.description || parsedResponse.message || 'Unable to add gift product.');
    }

    document.dispatchEvent(
      new CartAddEvent(parsedResponse, 'cart-gift-product', {
        source: 'cart-gift-product',
        variantId: String(variantId),
        sections: parsedResponse.sections,
      })
    );
  }

  onCartUpdate = (event) => {
    if (event?.detail?.source === 'cart-gift-product') return;

    const checkbox = this.querySelector('input[type="checkbox"]');
    const preserveLoading = checkbox instanceof HTMLInputElement && checkbox.checked;

    if (preserveLoading) {
      this.setLoadingState(true);
    }

    this.syncGiftProductState({ preserveLoading }).catch((error) => console.error(error));
  };

  async syncGiftProductState({ preserveLoading = false } = {}) {
    const variantId = Number(this.dataset.giftVariantId);
    if (!variantId) return;

    try {
      const cart = await this.getCart();
      const giftLine = this.findGiftLine(cart, variantId);
      const hasMainProducts = cart.items?.some((item) => item.properties?.[GIFT_PROPERTY] !== 'true') ?? false;
      const checkbox = this.querySelector('input[type="checkbox"]');

      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = Boolean(giftLine);
      }

      if (giftLine && !hasMainProducts) {
        await this.withLoading(() => this.removeGiftProduct(variantId, cart, giftLine));
      }
    } finally {
      if (preserveLoading) {
        this.setLoadingState(false);
      }
    }
  }

  async removeGiftProduct(variantId, cart = null, existingGiftLine = null) {
    const currentCart = cart || (await this.getCart());
    const giftLine = existingGiftLine || this.findGiftLine(currentCart, variantId);

    if (!giftLine) {
      this.dispatchCartUpdate(currentCart, {});
      return;
    }

    const sections = this.getSectionIds();
    const response = await fetch(Theme.routes.cart_change_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({
          id: giftLine.key,
          quantity: 0,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      }),
    });

    const parsedResponse = await response.json();

    if (!response.ok || parsedResponse.errors) {
      throw new Error(parsedResponse.errors || 'Unable to remove gift product.');
    }

    this.dispatchCartUpdate(parsedResponse, parsedResponse.sections);
  }

  async getCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Unable to read cart.');

    return response.json();
  }

  findGiftLine(cart, variantId) {
    return cart.items?.find((item) => {
      return Number(item.variant_id) === variantId && item.properties?.[GIFT_PROPERTY] === 'true';
    });
  }

  async withLoading(task) {
    this.setLoadingState(true);

    try {
      return await task();
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    this.#loadingCount = isLoading ? this.#loadingCount + 1 : Math.max(0, this.#loadingCount - 1);

    const loading = this.#loadingCount > 0;
    const drawerDialog = document.querySelector('.cart-drawer__dialog');
    const cartSummary = document.querySelector('.cart__summary-totals');
    // const cartCtas = document.querySelector('.cart__ctas');
    const checkoutButton = document.querySelector('.cart__checkout-button');
    const control = this.querySelector('.cart-gift-product__control');

    this.classList.toggle('is-loading', loading);
    if (drawerDialog instanceof HTMLElement) {
      drawerDialog.classList.toggle('is-loading', loading);
      if (loading) {
        drawerDialog.setAttribute('aria-busy', 'true');
      } else {
        drawerDialog.removeAttribute('aria-busy');
      }
    }

    [cartSummary].forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      element.classList.toggle('is-loading', loading);
      if (loading) {
        element.setAttribute('aria-busy', 'true');
      } else {
        element.removeAttribute('aria-busy');
      }
    });

    if (checkoutButton instanceof HTMLButtonElement) {
      if (loading) {
        if (this.#checkoutWasDisabled === null) {
          this.#checkoutWasDisabled = checkoutButton.disabled;
        }
        checkoutButton.disabled = true;
      } else {
        checkoutButton.disabled = this.#checkoutWasDisabled ?? false;
        this.#checkoutWasDisabled = null;
      }
    }

    if (control instanceof HTMLElement) {
      control.classList.toggle('is-loading', loading);
    }
  }

  dispatchCartUpdate(cart, sections) {

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'cart-gift-product', {
        source: 'cart-gift-product',
        itemCount: cart.item_count,
        sections,
      })
    );
  }
}

if (!customElements.get('cart-gift-product')) {
  customElements.define('cart-gift-product', CartGiftProduct);
}
